import { useState, useEffect } from 'react';
import useLunaStore from '../store/useLunaStore';
import api from '../services/api';

/**
 * Hook to handle session restoration and initialization
 * @returns {boolean} isInitialized - Whether the session restoration is complete
 */
export function useSessionRestore() {
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                // [Fix] 로컬 스토리지에서 사용자 ID 확인
                const localUserId = localStorage.getItem('coding_tutor_user_id');

                // 서버 세션 시작/복원 요청
                const sessionData = await api.startSession(localUserId, null);

                if (sessionData && sessionData.user) {
                    console.log('[APP] 세션 복원 성공:', sessionData);

                    // 1. 사용자 정보 및 게이미피케이션 상태 복원
                    useLunaStore.getState().setUserSession({
                        username: sessionData.user.username,
                        is_admin: sessionData.user.is_admin,
                        user_id: sessionData.session_id,
                        level: sessionData.gamification?.level || 1,
                        total_xp: sessionData.gamification?.xp || 0,
                        streak: sessionData.gamification?.streak || 0,
                        required_xp: sessionData.gamification?.requiredXp || 100,
                    });

                    // 로컬 스토리지 ID 동기화
                    if (sessionData.session_id) {
                        localStorage.setItem('coding_tutor_user_id', sessionData.session_id);
                    }

                    // 2. 학습 상태 복원 (Resume 모드인 경우)
                    // [Fix] 로드맵 토픽 전환 중이면 세션 복원 스킵 (handleStartLesson에서 설정한 새 상태를 보존)
                    if (sessionData.status === 'resume' && sessionData.state) {
                        // [Check] 이미 다른 곳에서 커리큘럼 생성을 요청했다면 복원하지 않음
                        const needsGeneration = useLunaStore.getState().needsCurriculumGeneration;
                        if (needsGeneration) {
                            console.log('[APP] 커리큘럼 생성 요청 대기 중이므로 세션 상태 복원 스킵');
                        } else {
                            const { state } = sessionData;
                            const store = useLunaStore.getState();

                            // 앱 상태 복원
                            if (state.currentLessonPlan) {
                                store.setCurrentLessonPlan(state.currentLessonPlan);
                                store.setCurrentLessonStepIndex(state.currentLessonStepIndex || 0);
                                store.setLessonCompleted(state.lessonCompleted || false);

                                if (!state.lessonCompleted) {
                                    store.setAppState('CONCEPT_VIEW');
                                }
                            }

                            if (state.currentCurriculum) {
                                store.setCurrentCurriculum(state.currentCurriculum);
                                store.setCurrentStepIndex(state.currentStepIndex || 0);
                                store.setIntendedTopic(state.intendedTopic);
                            }

                            // 채팅 기록 복원
                            if (state.chatHistory && state.chatHistory.length > 0) {
                                store.setChatHistory(state.chatHistory);
                            }

                            // 코드 스냅샷 복원
                            if (state.lastCodeSnapshot) {
                                store.setLastCodeSnapshot(state.lastCodeSnapshot);
                            }

                            // [Fix] 로드맵 모드 상태 복원
                            if (state.isRoadmapMode) {
                                console.log('[APP] 로드맵 모드 복원됨');
                                store.setIsRoadmapMode(true);

                                if (state.careerRoadmap) {
                                    store.setCurrentRoadmap(state.careerRoadmap);
                                }

                                if (state.roadmapContext) {
                                    console.log('[APP] 로드맵 컨텍스트 복원:', state.roadmapContext);
                                    store.setRoadmapContext(state.roadmapContext);
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn('[APP] 초기화 중 오류:', err);
            } finally {
                setIsInitialized(true);
            }
        };

        initializeApp();
    }, []); // 의존성 제거 (한 번만 실행)

    return isInitialized;
}
